import {
  MultiFactorInfo,
  MultiFactorUser,
  PhoneMultiFactorInfo,
  User,
  multiFactor,
  sendEmailVerification,
} from 'firebase/auth';
import { Button, IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../data/providers/popupsProvider';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { FormSection } from '../../reports/utils/settings/FormSection';
import { IListRowRenderProps, List } from '../../shared/List/List';
import { ListControlContainer } from '../../shared/List/ListControlContainer';
import {
  ListItem,
  ListItemCell,
  ListItemSafeText,
} from '../../shared/List/ListItem';
import { NoResults } from '../../shared/NoResults';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { EnrollmentPopup } from './EnrollmentPopup';

interface IRegisterMFAProps {
  user: User;
  error?: string;
}

interface ISecondFactorProps {
  factor: MultiFactorInfo;
  style?: React.CSSProperties;
  unenrollHandler: (factor: MultiFactorInfo) => void;
}

const secondFactorLabel = (factor: MultiFactorInfo): string => {
  switch (factor.factorId) {
    case 'phone':
      return `SMS: ${(factor as PhoneMultiFactorInfo).phoneNumber}`;
      break;
  }
};

const SecondFactor = (props: ISecondFactorProps): React.ReactElement => {
  const { factor, style, unenrollHandler } = props;

  const unenroll = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    unenrollHandler(factor);
  };

  return (
    <ListItem style={{ height: '100px', ...style }}>
      <ListItemCell style={{ flex: 2 }}>
        <Row style={{ alignItems: 'center' }}>
          <Column>
            <div>
              <span>{factor.displayName}</span>
            </div>
            <Description>
              <ListItemSafeText>{secondFactorLabel(factor)}</ListItemSafeText>
            </Description>
          </Column>
        </Row>
      </ListItemCell>
      <ListItemCell style={{ flex: 1 }}>
        <IconButton onClick={unenroll} icon='clear' title='Unenroll' />
      </ListItemCell>
    </ListItem>
  );
};

const Description = styled.div`
  color: var(--color-secondary-text);
`;

export const MultiFactor = withRouter(
  (props: IRegisterMFAProps & RouteComponentProps): React.ReactElement => {
    const [multiFactorUser, setMultifactorUser] =
      React.useState<MultiFactorUser | null>(null);

    React.useEffect((): void => {
      if (props.user) {
        setMultifactorUser(multiFactor(props.user));
      }
    }, [props.user]);

    const unenrollFactor = async (factor: MultiFactorInfo): Promise<void> => {
      if (multiFactorUser) {
        await multiFactorUser.unenroll(factor);
        window.location.reload();
      }
    };

    /**
     * @description render function for list items
     * @param param0
     */
    const rowRenderer = ({
      index,
      key,
      style,
    }: IListRowRenderProps): React.ReactElement => {
      const row = multiFactorUser?.enrolledFactors?.[index];

      return (
        <SecondFactor
          factor={row}
          key={key}
          style={style}
          unenrollHandler={unenrollFactor}
        />
      );
    };

    const sendVerifyEmail = async (): Promise<void> => {
      await sendEmailVerification(props?.user);
      window.alert(
        'Check you email for verification link. You will need to reload the page after verifying your email.'
      );
      window.location.reload();
    };

    const enrollSecondFactor = (): void => {
      openInnerPopup(
        <EnrollmentPopup
          user={props.user}
          onCancel={closeInnerPopup(props.history)}
        />,
        props.history,
        'enrollment'
      );
    };

    return (
      <FormSection style={{ flex: 1, height: '100%' }}>
        <div>
          <h5 style={{ flex: 1 }}>Multi-Factor Authentication</h5>
          <div>
            {props.user?.emailVerified ? (
              <Button
                style={{ marginTop: '10px' }}
                title='Enroll Second Factor'
                onClick={(): void => enrollSecondFactor()}
              />
            ) : (
              <Button
                style={{ marginTop: '10px' }}
                title='Verify Email to Enroll Second Factor'
                onClick={(): Promise<void> => sendVerifyEmail()}
                icon='mark_email_read'
              />
            )}
          </div>
        </div>
        <hr />
        <Row style={{ height: '100%' }}>
          <div style={{ height: '100%', flex: 1 }}>
            <ControlSpacer style={{ display: 'flex', height: '100%' }}>
              <ListControlContainer style={{ flex: 1, height: '100%' }}>
                {multiFactorUser?.enrolledFactors?.length > 0 ? (
                  <List
                    rowHeight={LIST_ROW_HEIGHTS.normal}
                    rowRenderer={rowRenderer}
                    listLength={multiFactorUser?.enrolledFactors?.length}
                  />
                ) : (
                  <React.Fragment>
                    <NoResults
                      message='No Second Factors Enrolled'
                      filterApplied={false}
                      clearFilter={(): void => null}
                    />
                  </React.Fragment>
                )}
              </ListControlContainer>
            </ControlSpacer>
          </div>
        </Row>
      </FormSection>
    );
  }
);
